import type { ItemUnit } from '../types';

/**
 * Unit mapping from Open Food Facts to our supported units.
 * Keys are lowercase, normalized OFF unit strings.
 */
const OFF_UNIT_MAP: Record<string, ItemUnit> = {
  // Weight - metric
  g: 'g',
  gram: 'g',
  grams: 'g',
  gr: 'g',
  kg: 'kg',
  kilogram: 'kg',
  kilograms: 'kg',
  kilo: 'kg',
  // Weight - imperial
  oz: 'oz',
  ounce: 'oz',
  ounces: 'oz',
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  // Volume - metric
  ml: 'ml',
  milliliter: 'ml',
  milliliters: 'ml',
  millilitre: 'ml',
  millilitres: 'ml',
  l: 'l',
  liter: 'l',
  liters: 'l',
  litre: 'l',
  litres: 'l',
  // Volume - centiliter (convert to ml)
  cl: 'ml',
  centiliter: 'ml',
  centiliters: 'ml',
  centilitre: 'ml',
  centilitres: 'ml',
  // Volume - imperial
  floz: 'floz',
  'fl oz': 'floz',
  'fluid ounce': 'floz',
  'fluid ounces': 'floz',
  cup: 'cup',
  cups: 'cup',
  pt: 'pt',
  pint: 'pt',
  pints: 'pt',
  qt: 'qt',
  quart: 'qt',
  quarts: 'qt',
  gal: 'gal',
  gallon: 'gal',
  gallons: 'gal',
  // Count/pieces
  item: 'item',
  items: 'item',
  piece: 'item',
  pieces: 'item',
  pc: 'item',
  pcs: 'item',
  unit: 'item',
  units: 'item',
};

/**
 * Maps an Open Food Facts unit string to our supported ItemUnit.
 * Case-insensitive, handles common abbreviations and variations.
 *
 * @param offUnit - The unit string from OFF API (e.g., "ml", "KG", "pieces")
 * @returns The mapped ItemUnit or null if not mappable
 */
export function mapOffUnitToItemUnit(offUnit: string | null | undefined): ItemUnit | null {
  if (!offUnit) return null;
  const normalized = offUnit.toLowerCase().trim();
  return OFF_UNIT_MAP[normalized] || null;
}

/**
 * Converts quantity when unit mapping requires a conversion.
 * Currently handles: cl -> ml (multiply by 10)
 *
 * @param quantity - Original quantity value
 * @param fromUnit - Original unit string from OFF
 * @param toUnit - Target ItemUnit after mapping
 * @returns Converted quantity
 */
export function convertQuantityForUnit(
  quantity: number,
  fromUnit: string,
  toUnit: ItemUnit
): number {
  const from = fromUnit.toLowerCase().trim();

  // Centiliter to milliliter: 1 cl = 10 ml
  if ((from === 'cl' || from === 'centiliter' || from === 'centilitre') && toUnit === 'ml') {
    return quantity * 10;
  }

  return quantity;
}

/**
 * Format quantity for user-friendly display.
 * Converts to larger units when appropriate (g to kg, ml to L).
 *
 * @param quantity - The quantity value in base units
 * @param unit - The unit type
 * @returns Formatted string with appropriate unit (e.g., '2.5 kg')
 *
 * @example
 * formatQuantityDisplay(2500, 'g')  // '2.5 kg'
 * formatQuantityDisplay(750, 'ml')  // '750 mL'
 * formatQuantityDisplay(1500, 'ml') // '1.5 L'
 */
export function formatQuantityDisplay(quantity: number, unit: ItemUnit): string {
  // Weight conversions: g to kg when >= 1000g
  if (unit === 'g' && quantity >= 1000) {
    const kg = quantity / 1000;
    return `${kg % 1 === 0 ? kg.toFixed(0) : kg.toFixed(1)} kg`;
  }
  // kg to g when < 1kg
  if (unit === 'kg' && quantity < 1) {
    return `${(quantity * 1000).toFixed(0)} g`;
  }

  // Volume conversions: ml to L when >= 1000ml
  if (unit === 'ml' && quantity >= 1000) {
    const l = quantity / 1000;
    return `${l % 1 === 0 ? l.toFixed(0) : l.toFixed(1)} L`;
  }
  // L to ml when < 1L
  if (unit === 'l' && quantity < 1) {
    return `${(quantity * 1000).toFixed(0)} mL`;
  }

  // Imperial: oz to lb when >= 16oz
  if (unit === 'oz' && quantity >= 16) {
    return `${(quantity / 16).toFixed(1)} lb`;
  }

  // Imperial volume: floz to cup when >= 8 fl oz
  if (unit === 'floz' && quantity >= 8) {
    const cups = quantity / 8;
    return `${cups % 1 === 0 ? cups.toFixed(0) : cups.toFixed(1)} cup${cups === 1 ? '' : 's'}`;
  }

  // Imperial volume: cup to qt when >= 4 cups
  if (unit === 'cup' && quantity >= 4) {
    const qt = quantity / 4;
    return `${qt % 1 === 0 ? qt.toFixed(0) : qt.toFixed(1)} qt`;
  }

  // Imperial volume: pt to qt when >= 2 pints
  if (unit === 'pt' && quantity >= 2) {
    const qt = quantity / 2;
    return `${qt % 1 === 0 ? qt.toFixed(0) : qt.toFixed(1)} qt`;
  }

  // Imperial volume: qt to gal when >= 4 quarts
  if (unit === 'qt' && quantity >= 4) {
    const gal = quantity / 4;
    return `${gal % 1 === 0 ? gal.toFixed(0) : gal.toFixed(1)} gal`;
  }

  // Default: return as-is with unit label
  const unitLabels: Record<ItemUnit, string> = {
    g: 'g',
    kg: 'kg',
    ml: 'mL',
    l: 'L',
    oz: 'oz',
    lb: 'lb',
    floz: 'fl oz',
    cup: quantity === 1 ? 'cup' : 'cups',
    pt: 'pt',
    qt: 'qt',
    gal: 'gal',
    item: quantity === 1 ? 'item' : 'items',
  };
  return `${quantity} ${unitLabels[unit] || unit}`;
}

/**
 * Get appropriate step size for quantity adjustments based on unit.
 *
 * @param unit - The unit type
 * @returns Step size for increment/decrement operations
 */
export function getQuantityStepSize(unit: ItemUnit): number {
  switch (unit) {
    case 'g':
      return 50; // 50g increments
    case 'kg':
      return 0.1; // 100g increments (0.1kg)
    case 'ml':
      return 50; // 50ml increments
    case 'l':
      return 1; // 1L increments (use mL for smaller amounts)
    case 'oz':
      return 1; // 1oz increments
    case 'lb':
      return 0.25; // 1/4 lb increments
    case 'floz':
      return 1; // 1 fl oz increments
    case 'cup':
      return 0.25; // 1/4 cup increments
    case 'pt':
      return 0.5; // 1/2 pint increments
    case 'qt':
      return 0.5; // 1/2 quart increments
    case 'gal':
      return 0.25; // 1/4 gallon increments
    case 'item':
    default:
      return 1;
  }
}

/**
 * Common cooking amounts for each unit type.
 * These represent typical measurements used when cooking/baking.
 * Ordered by priority - most commonly used amounts first.
 */
const COMMON_COOKING_AMOUNTS: Record<ItemUnit, number[]> = {
  g: [100, 200, 250, 500, 50, 150, 300, 750, 25, 1000],
  kg: [0.5, 1, 0.25, 0.75, 0.1, 1.5, 2],
  oz: [4, 8, 2, 6, 1, 12, 16],
  lb: [0.5, 1, 0.25, 0.75, 1.5, 2],
  ml: [100, 250, 500, 50, 200, 125, 150, 300, 750, 30, 15, 1000],
  l: [0.5, 1, 0.25, 0.75, 1.5, 2],
  floz: [4, 8, 2, 6, 1, 12, 16],
  cup: [1, 0.5, 0.25, 0.75, 2, 1.5, 0.33, 0.66, 3, 4],
  pt: [1, 0.5, 1.5, 2],
  qt: [1, 0.5, 1.5, 2],
  gal: [0.5, 1, 0.25],
  item: [1, 2, 3, 4, 5, 6],
};

/**
 * Generate quick select options based on unit type and max quantity.
 * Returns common cooking measurements appropriate for the unit type.
 * Amounts are ordered by priority (most commonly used first).
 *
 * @param unit - The unit type
 * @param maxQty - Maximum available quantity
 * @returns Array of { value, label } options for quick selection, always ending with "All"
 */
export function getQuickSelectOptions(
  unit: ItemUnit,
  maxQty: number
): Array<{ value: number; label: string }> {
  const commonAmounts = COMMON_COOKING_AMOUNTS[unit] || [1];

  const filtered = commonAmounts.filter((amount) => amount > 0 && amount < maxQty - 0.001);
  const selected = filtered.slice(0, 5).sort((a, b) => a - b);

  const formatLabel = (value: number): string => {
    if (['cup', 'floz', 'pt', 'qt', 'gal', 'oz', 'lb'].includes(unit)) {
      return formatQuantityWithFractions(value, unit);
    }
    if (unit === 'g' || unit === 'ml') {
      return `${value}${unit}`;
    }
    if (unit === 'kg') {
      return value < 1 ? `${value * 1000}g` : `${value}kg`;
    }
    if (unit === 'l') {
      return value < 1 ? `${value * 1000}mL` : `${value}L`;
    }
    return `${value}`;
  };

  const options = selected.map((value) => ({
    value,
    label: formatLabel(value),
  }));

  options.push({ value: maxQty, label: 'All' });

  return options;
}

// ============================================================
// Expression Parser
// ============================================================

/**
 * Safely parse and evaluate simple math expressions using a recursive descent parser.
 * Supports: +, -, *, /, parentheses, decimals.
 *
 * @param input - The expression string (e.g., "2*250", "500+250", "(2+1)*100")
 * @returns The evaluated result, or null if invalid/cannot parse
 *
 * @example
 * parseQuantityExpression("2*250")     // 500
 * parseQuantityExpression("500+250")   // 750
 * parseQuantityExpression("3/4")       // 0.75
 * parseQuantityExpression("(2+1)*100") // 300
 * parseQuantityExpression("abc")       // null
 * parseQuantityExpression("1/0")       // null (division by zero)
 */
export function parseQuantityExpression(input: string): number | null {
  const expr = input.replace(/\s/g, '');

  if (!/^[\d+\-*/().]+$/.test(expr)) {
    return null;
  }

  if (!expr || /[+\-*/]{2,}/.test(expr) || /^[*/]/.test(expr) || /[+\-*/]$/.test(expr)) {
    return null;
  }

  try {
    const result = computeExpression(expr);

    if (result === null || !isFinite(result) || isNaN(result)) {
      return null;
    }

    if (result < 0) {
      return null;
    }

    return Math.round(result * 1000) / 1000;
  } catch {
    return null;
  }
}

/**
 * Recursive descent parser for math expressions.
 * Grammar:
 *   expr   -> term (('+' | '-') term)*
 *   term   -> factor (('*' | '/') factor)*
 *   factor -> NUMBER | '(' expr ')'
 */
function computeExpression(expr: string): number | null {
  let pos = 0;

  function parseExpr(): number | null {
    let left = parseTerm();
    if (left === null) return null;

    while (pos < expr.length && (expr[pos] === '+' || expr[pos] === '-')) {
      const op = expr[pos];
      pos++;
      const right = parseTerm();
      if (right === null) return null;
      left = op === '+' ? left + right : left - right;
    }
    return left;
  }

  function parseTerm(): number | null {
    let left = parseFactor();
    if (left === null) return null;

    while (pos < expr.length && (expr[pos] === '*' || expr[pos] === '/')) {
      const op = expr[pos];
      pos++;
      const right = parseFactor();
      if (right === null) return null;
      if (op === '/') {
        if (right === 0) return null;
        left = left / right;
      } else {
        left = left * right;
      }
    }
    return left;
  }

  function parseFactor(): number | null {
    if (expr[pos] === '(') {
      pos++;
      const result = parseExpr();
      if (result === null || expr[pos] !== ')') return null;
      pos++;
      return result;
    }

    let negative = false;
    if (expr[pos] === '-') {
      negative = true;
      pos++;
    }

    const start = pos;
    while (pos < expr.length && (/\d/.test(expr[pos]) || expr[pos] === '.')) {
      pos++;
    }

    if (start === pos) return null;

    const numStr = expr.slice(start, pos);
    const num = parseFloat(numStr);
    if (isNaN(num)) return null;

    return negative ? -num : num;
  }

  const result = parseExpr();

  if (pos !== expr.length) {
    return null;
  }

  return result;
}

// ============================================================
// Fractional Display
// ============================================================

/**
 * Unicode fraction characters for common fractions.
 */
const FRACTION_MAP: Array<{ decimal: number; fraction: string; tolerance: number }> = [
  { decimal: 0.125, fraction: '\u215B', tolerance: 0.02 },
  { decimal: 0.25, fraction: '\u00BC', tolerance: 0.02 },
  { decimal: 0.333, fraction: '\u2153', tolerance: 0.02 },
  { decimal: 0.375, fraction: '\u215C', tolerance: 0.02 },
  { decimal: 0.5, fraction: '\u00BD', tolerance: 0.02 },
  { decimal: 0.625, fraction: '\u215D', tolerance: 0.02 },
  { decimal: 0.666, fraction: '\u2154', tolerance: 0.02 },
  { decimal: 0.75, fraction: '\u00BE', tolerance: 0.02 },
  { decimal: 0.875, fraction: '\u215E', tolerance: 0.02 },
];

/**
 * Imperial units that should display with fractions.
 */
const IMPERIAL_UNITS: ItemUnit[] = ['floz', 'cup', 'pt', 'qt', 'gal', 'oz', 'lb'];

/**
 * Format a quantity with fractions for imperial units.
 * Metric units always display as decimals.
 *
 * @param quantity - The quantity to format
 * @param unit - The unit type
 * @param options - Optional settings
 * @returns Formatted string (e.g., "1/2 cup", "1.5 cups", "0.5 L")
 */
export function formatQuantityWithFractions(
  quantity: number,
  unit: ItemUnit,
  options?: { useFractions?: boolean }
): string {
  const useFractions = options?.useFractions ?? true;

  const isImperial = IMPERIAL_UNITS.includes(unit);

  if (!isImperial || !useFractions) {
    return formatQuantityDisplay(quantity, unit);
  }

  const wholePart = Math.floor(quantity);
  const fractionalPart = quantity - wholePart;

  let fractionStr: string | null = null;
  for (const { decimal, fraction, tolerance } of FRACTION_MAP) {
    if (Math.abs(fractionalPart - decimal) < tolerance) {
      fractionStr = fraction;
      break;
    }
  }

  const unitLabel = getUnitLabelPlural(unit, quantity);

  if (fractionStr) {
    if (wholePart === 0) {
      return `${fractionStr} ${unitLabel}`;
    }
    return `${wholePart}${fractionStr} ${unitLabel}`;
  }

  if (fractionalPart === 0) {
    return `${wholePart} ${unitLabel}`;
  }

  const rounded = Math.round(quantity * 100) / 100;
  return `${rounded} ${unitLabel}`;
}

/**
 * Get pluralized unit label.
 */
function getUnitLabelPlural(unit: ItemUnit, quantity: number): string {
  const isPlural = quantity !== 1;

  const labels: Record<ItemUnit, { singular: string; plural: string }> = {
    item: { singular: 'item', plural: 'items' },
    g: { singular: 'g', plural: 'g' },
    kg: { singular: 'kg', plural: 'kg' },
    ml: { singular: 'mL', plural: 'mL' },
    l: { singular: 'L', plural: 'L' },
    oz: { singular: 'oz', plural: 'oz' },
    lb: { singular: 'lb', plural: 'lb' },
    floz: { singular: 'fl oz', plural: 'fl oz' },
    cup: { singular: 'cup', plural: 'cups' },
    pt: { singular: 'pt', plural: 'pt' },
    qt: { singular: 'qt', plural: 'qt' },
    gal: { singular: 'gal', plural: 'gal' },
  };

  const label = labels[unit] || { singular: unit, plural: unit };
  return isPlural ? label.plural : label.singular;
}
